import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router';
import { Action, BrowserHistory } from 'history';
import { get as _get } from 'lodash/object';
import LegacyLoading from '@components/core/loading';
import { FullPageInfoField } from '@components-clean/core/info-field';
import DefaultLoading from '@components-clean/core/loading/loading';
import {
    getMonitoringToolkitHelper,
    MonitoringToolkit,
} from '@components-clean/core/monitoring-toolkit/monitoring-toolkit';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import { getAllLeafNavItems, pageNavItems } from '@components-clean/page-nav/page-nav-menu-items.data';
import { useLoading } from '@contexts-clean/loader-context';
import { PageToolbarContextProvider, usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { leftJoinAndMergeArrays } from '@utils-clean/array-helpers';
import { clearPageTabsState, clearPageTabsStateLegacy } from '@utils-clean/page-tab-helpers';
import { isStudyMember } from '@utils-clean/studyMember-helper';
import { FeatureContext } from '@contexts/feature-context';
import { NullableLegacyDataset, NullableStudy } from '@contexts/local-state-context';
import { IUser, Permissions } from '@contexts/user-context';
import { useLayout } from '@hooks/index';
import Constants from '@utils/constants';
import LayoutProvider from '@utils/layouts';
import Modules, { IModule } from '@config/modules';
import AdminRoutes from '@config/routes/admin';
import { IRoute } from '@config/routes/interfaces';
import LoginRoutes from '@config/routes/login';
import { isCM2Page, isLegacyCMPage, isSMPage, requireDataset, requireStudy } from '@config/routes/page-data-helpers';
import PrivateRoutes from '@config/routes/private';
import PublicRoutes from '@config/routes/public';
import { KendoTheme } from '@config/theme';

// The following component ensures that the breadcrumb remains at the top of the page when the content is scrolled.
// It seems like we need to come up with a better way to handle all these nav type things,
// but for now this ensures popped out pages (i.e. without the toolkit) behave in the same way.
const PoppedOutContent = ({ children }: PropsWithChildren) => (
    <div className="full-content-height-with-scroll">
        <div style={{ height: '100%', overflow: 'auto', padding: '10px' }}>{children}</div>
    </div>
);

const PaddedContent = ({ children }: PropsWithChildren) => (
    <div className="full-content-height-with-scroll" style={{ padding: 10 }}>
        {children}
    </div>
);

const LegacyPaddedContent = ({ children }: PropsWithChildren) => (
    <div className="legacy-full-content-height-with-scroll" style={{ padding: 10 }}>
        {children}
    </div>
);

const getComponent = (
    user: IUser | null | undefined,
    study: NullableStudy | undefined,
    currentTheme: KendoTheme,
    legacyDataset: NullableLegacyDataset | undefined,
    cm2Dataset: CM2Dataset | null | undefined,
    route: IRoute,
    organisationKey?: string,
) => {
    const { t } = useTranslation();
    const { hasFeature } = useContext(FeatureContext);

    const {
        component: Component,
        componentFn,
        module,
        moduleValue,
        element,
        feature,
        path,
        isCentralMonitoringToolkitEnabled,
        isSubjectMonitoringToolkitEnabled,
        isRiskAssessmentToolkitEnabled,
        include,
        exclude,
    } = route;

    const { isLoading } = useLoading();

    if (!user) return <Navigate to="/login" replace />;

    let isIncluded = true;
    let isExcluded = false;

    if (user) {
        if (include) isIncluded = include.indexOf(user.systemAdminLevel) >= 0;
        if (exclude) isExcluded = exclude.indexOf(user.systemAdminLevel) >= 0;
    }

    if (isLoading) {
        if (currentTheme === 'default')
            return (
                <div className="opra-default-theme k-h-full k-w-full">
                    <DefaultLoading />
                </div>
            );
        return <LegacyLoading />;
    }

    if (feature && !hasFeature(feature.id))
        return currentTheme === 'default' ? (
            <FullPageInfoField message={t('home:pageDataErrors:featureNotEnabled')} type="Info" />
        ) : (
            <div style={{ padding: '10px' }}>{t('home:pageDataErrors:featureNotEnabled')}</div>
        );

    if (
        (module &&
            (!user?.hasModule?.(module) || (moduleValue && study && !isStudyMember(moduleValue, study, user)))) ||
        (element &&
            moduleValue &&
            !user?.can?.({
                module: moduleValue,
                element,
                permission: Permissions.Enabled,
            })) ||
        !isIncluded ||
        isExcluded ||
        (user.isSystemAdmin(Constants.SystemAdminLevel.Administrator) &&
            user?.admininstratorCanViewRoute &&
            !user.admininstratorCanViewRoute(route)) ||
        (user?.nonAdmininstratorCanViewRoute && !user.nonAdmininstratorCanViewRoute(route))
    ) {
        return currentTheme === 'default' ? (
            <FullPageInfoField message={t('home:pageDataErrors:cannotAccessModule')} type="Info" />
        ) : (
            <div style={{ padding: '10px' }}>{t('home:pageDataErrors:cannotAccessModule')}</div>
        );
    }
    if (!study && requireStudy(path))
        return currentTheme === 'default' ? (
            <FullPageInfoField message={t('home:pageDataErrors:noStudies')} type="Info" />
        ) : (
            <div style={{ padding: '10px' }}>{t('home:pageDataErrors:noStudies')}</div>
        );

    if (requireDataset(path) && isCM2Page(path) && !cm2Dataset)
        return <FullPageInfoField message={t('home:pageDataErrors:noDatasets')} type="Info" />;

    if (requireDataset(path) && isLegacyCMPage(path) && !legacyDataset)
        return <div style={{ padding: '10px' }}>{t('home:pageDataErrors:noDatasets')}</div>;

    if (requireDataset(path) && isSMPage(path) && !cm2Dataset?.isSmEnabledDataset)
        return <FullPageInfoField message={t('home:pageDataErrors:noSubjectMonitoringDataset')} type="Info" />;

    const component = componentFn ? (
        componentFn({ user, study, organisationKey, legacyDataset, cm2Dataset })
    ) : (
        <Component
            user={user}
            study={study}
            organisationKey={organisationKey}
            legacyDataset={legacyDataset}
            cm2Dataset={cm2Dataset}
        />
    );

    // When navigating away from the CM2 or SM module,
    // We want to clear the monitoring toolkit open state to ensure
    // it is minimised when the user returns to the module.
    if ((module as IModule) === Modules.CentralMonitoring2) {
        const cmHelper = getMonitoringToolkitHelper(module as IModule);
        cmHelper.clearMonitoringToolKitOpenState(user);
    }
    if ((module as IModule) === Modules.SubjectMonitoring) {
        const smHelper = getMonitoringToolkitHelper(module as IModule);
        smHelper.clearMonitoringToolKitOpenState(user);
    }
    if ((module as IModule) === Modules.RiskAssessment) {
        const ramHelper = getMonitoringToolkitHelper(module as IModule);
        ramHelper.clearMonitoringToolKitOpenState(user);
    }
    if (currentTheme === 'default') {
        let content = component;
        const toolkitHelper = getMonitoringToolkitHelper(module as IModule);

        if (IsPopOut()) {
            content = <PoppedOutContent>{component}</PoppedOutContent>;
        } else if (
            (isCentralMonitoringToolkitEnabled ||
                isSubjectMonitoringToolkitEnabled ||
                isRiskAssessmentToolkitEnabled) &&
            (user?.isSystemAdmin(Constants.SystemAdminLevel.Administrator) ||
                toolkitHelper.canUserViewToolkit(user, module?.value as number))
        ) {
            content = (
                <div key={toolkitHelper.TOOLKIT_STATE_KEY}>
                    <MonitoringToolkit
                        module={module as IModule}
                        user={user}
                        organisationKey={organisationKey}
                        study={study}
                        cm2Dataset={cm2Dataset}
                    >
                        {component}
                    </MonitoringToolkit>
                </div>
            );
        } else {
            content = <PaddedContent>{component}</PaddedContent>;
        }

        return <div className="opra-default-theme">{content}</div>;
    }

    return <LegacyPaddedContent>{component}</LegacyPaddedContent>;
};

interface PublicPageProps {
    component: React.ComponentType<{
        className?: string;
    }>;
    componentFn?: () => React.ReactNode;
}

const PublicPage = ({ component: Component, componentFn }: PublicPageProps) =>
    componentFn ? componentFn() : <Component />;

interface PrivatePageProps {
    user?: IUser | null;
    organisationKey?: string;
    study: NullableStudy | undefined;
    legacyDataset: NullableLegacyDataset | undefined;
    cm2Dataset: CM2Dataset | null | undefined;
    currentTheme: KendoTheme;
    setCurrentTheme: (newTheme: KendoTheme) => void;
    route: IRoute;
}

const PrivatePage = ({
    user,
    organisationKey,
    study,
    legacyDataset,
    cm2Dataset,
    currentTheme,
    setCurrentTheme,
    route,
}: PrivatePageProps) => {
    const { dispatch } = useLayout();
    const { dispatch: pageToolbarDispatch } = usePageToolbar();
    const { tabs, breadcrumbs, pageBreadcrumbs, theme, pageToolbarComponents, module } = route;
    const location = useLocation();

    useEffect(() => {
        const newTheme = theme ?? 'legacy';

        if (newTheme !== currentTheme) {
            setCurrentTheme(newTheme);
        }
    }, [location]);

    useEffect(() => {
        dispatch({ type: 'SET_TABS', tabs: tabs || [] });
    }, [dispatch, tabs]);

    useEffect(() => {
        dispatch({ type: 'SET_BREADCRUMBS', breadcrumbs: breadcrumbs || [] });
    }, [dispatch, breadcrumbs]);

    useEffect(() => {
        dispatch({ type: 'SET_PAGE_BREADCRUMBS', pageBreadcrumbs: pageBreadcrumbs || [] });
    }, [dispatch, pageBreadcrumbs]);

    const components = pageToolbarComponents
        ?.filter((component) => {
            if (component.permissionElement) {
                return (
                    user?.can?.({
                        module: module!.value,
                        element: component.permissionElement,
                        permission: Permissions.Enabled,
                    }) ?? false
                );
            }

            return true;
        })
        .map((x) => x.component);

    useEffect(() => {
        pageToolbarDispatch({ type: 'setComponents', components });
    }, [pageToolbarComponents]);

    return getComponent(user, study, currentTheme, legacyDataset, cm2Dataset, route, organisationKey);
};

const AdminPage = ({
    user,
    organisationKey,
    study,
    legacyDataset,
    cm2Dataset,
    currentTheme,
    setCurrentTheme,
    route,
}: PrivatePageProps) => {
    const { tabs, breadcrumbs, theme, pageBreadcrumbs, pageToolbarComponents } = route;

    const location = useLocation();

    useEffect(() => {
        const newTheme = theme ?? 'legacy';

        if (newTheme !== currentTheme) {
            setCurrentTheme(newTheme);
        }
    }, [location]);

    const { dispatch } = useLayout();
    const { dispatch: pageToolbarDispatch } = usePageToolbar();

    useEffect(() => {
        dispatch({ type: 'SET_TABS', tabs: tabs || [] });
    }, [dispatch, tabs]);

    useEffect(() => {
        dispatch({ type: 'SET_BREADCRUMBS', breadcrumbs: breadcrumbs || [] });
    }, [dispatch, breadcrumbs]);

    useEffect(() => {
        dispatch({ type: 'SET_PAGE_BREADCRUMBS', pageBreadcrumbs: pageBreadcrumbs || [] });
    }, [dispatch, pageBreadcrumbs]);

    useEffect(() => {
        pageToolbarDispatch({
            type: 'setComponents',
            components: pageToolbarComponents?.map((x) => x.component),
        });
    }, [pageToolbarComponents]);

    return getComponent(user, study, currentTheme, legacyDataset, cm2Dataset, route, organisationKey);
};

interface IRouterProps {
    user?: IUser | null;
    organisationKey?: string;
    study?: NullableStudy;
    legacyDataset?: NullableLegacyDataset | undefined;
    cm2Dataset?: CM2Dataset | null | undefined;
    token?: string | null;
    history: BrowserHistory;
    currentTheme: KendoTheme;
    setCurrentTheme: (theme: KendoTheme) => void;
}

export type RouteLayoutType = 'DEFAULT' | 'PUBLIC' | 'PRIVATE' | 'LOGIN';

const Router = ({
    user,
    organisationKey,
    study,
    legacyDataset,
    cm2Dataset,
    token,
    history,
    currentTheme,
    setCurrentTheme,
}: IRouterProps) => {
    const [layout, setLayout] = useState<RouteLayoutType>('DEFAULT');
    const [currentRoute, setCurrentRoute] = useState<IRoute | null>(null);
    const location = useLocation();

    const isUndefinedNull = (variable: any) => variable === null || variable === undefined;

    useEffect(() => {
        const getRoute = () =>
            [...AdminRoutes, ...PrivateRoutes, ...LoginRoutes, ...PublicRoutes].find((route) => {
                if (route.path === location.pathname) return true;

                const match = matchPath(
                    {
                        path: route.path,
                        end: route.exact,
                        caseSensitive: true,
                    },
                    location.pathname,
                );
                return match ? route : null;
            });

        /**
         * Handling when browserHistory just created and no layout.key in the location object
         * the condition is fulfilled when user first time visit or enter the the app url
         * for example: user enter '/app/map' in the address bar and the map page uses a different layout
         * Switch will reset every children Route location property at the very beginning
         */

        const route = getRoute();
        if (!route) {
            throw new Error('Route unknown.');
        }
        setCurrentRoute(route);

        if (history.action === Action.Pop && isUndefinedNull(location.key)) {
            const routeLayout = _get(route, 'location.state.layout') as RouteLayoutType;

            if (routeLayout) setLayout(routeLayout);
        } else if (_get(location, 'state.layout')) setLayout(location.state.layout);
        else if (user && !user.forceResetPassword && token) setLayout('PRIVATE');
        else setLayout('LOGIN');

        if (window.location.pathname.match('login')) {
            setLayout('LOGIN');
        }
    }, [location, user]);

    useEffect(() => {
        clearPageTabsStateLegacy();
        return () => {
            clearPageTabsState(location.key);
        };
    }, [location.pathname, location.key]);

    const pageNavLeaves = getAllLeafNavItems(pageNavItems());
    const mergedPrivateRoutes = leftJoinAndMergeArrays(PrivateRoutes, pageNavLeaves, (r) => r.key);
    const mergedAdminRoutes = leftJoinAndMergeArrays(AdminRoutes, pageNavLeaves, (r) => r.key);

    return (
        <PageToolbarContextProvider>
            <LayoutProvider layout={layout} route={currentRoute} study={study} currentTheme={currentTheme}>
                <Routes>
                    {mergedPrivateRoutes.map((route) => (
                        <Route
                            {...route}
                            key={route.key}
                            element={
                                <PrivatePage
                                    user={user}
                                    organisationKey={organisationKey}
                                    study={study}
                                    legacyDataset={legacyDataset}
                                    cm2Dataset={cm2Dataset}
                                    currentTheme={currentTheme}
                                    setCurrentTheme={setCurrentTheme}
                                    route={route}
                                />
                            }
                        />
                    ))}
                    {mergedAdminRoutes.map((route) => (
                        <Route
                            {...route}
                            key={route.path}
                            element={
                                <AdminPage
                                    user={user}
                                    organisationKey={organisationKey}
                                    study={study}
                                    legacyDataset={legacyDataset}
                                    cm2Dataset={cm2Dataset}
                                    currentTheme={currentTheme}
                                    setCurrentTheme={setCurrentTheme}
                                    route={route}
                                />
                            }
                        />
                    ))}
                    {[...LoginRoutes, ...PublicRoutes].map((route) => (
                        <Route
                            {...route}
                            key={route.path}
                            element={<PublicPage component={route.component} componentFn={route.componentFn} />}
                        />
                    ))}
                </Routes>
            </LayoutProvider>
        </PageToolbarContextProvider>
    );
};

export default Router;
